import { Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import { getVulnerabilityColor, getTypeEmoji } from './map-utils'

export interface Property {
  id: string
  lat: number
  lng: number
  type: "residential" | "commercial" | "industrial"
  vulnerability: "low" | "medium" | "high" | "critical"
  floodDepth: number
  value: number
  name: string
}

interface PropertyMarkersProps {
  properties: Property[]
  showMarkers: boolean
  showPopups: boolean
  onAssetSelect: (asset: Property) => void
}

/**
 * Create custom marker icon for properties
 */
function createMarkerIcon(property: Property) {
  const color = getVulnerabilityColor(property.vulnerability)
  const emoji = getTypeEmoji(property.type)

  return L.divIcon({
    html: `<div style="
      background-color: ${color}; 
      width: 28px; 
      height: 28px; 
      border-radius: 50%; 
      display: flex; 
      align-items: center; 
      justify-content: center; 
      font-size: 14px; 
      border: 3px solid white; 
      box-shadow: 0 3px 6px rgba(0,0,0,0.3);
      transition: transform 0.2s ease;
    " class="enhanced-marker">${emoji}</div>`,
    iconSize: [28, 28],
    className: 'custom-div-icon'
  })
}

/**
 * Component to render property markers on the map
 */
export function PropertyMarkers({
  properties,
  showMarkers,
  showPopups,
  onAssetSelect
}: PropertyMarkersProps) {
  if (!showMarkers) return null

  return (
    <>
      {properties.map(property => (
        <Marker
          key={property.id}
          position={[property.lat, property.lng]}
          icon={createMarkerIcon(property)}
          eventHandlers={{
            click: () => onAssetSelect(property)
          }}
        >
          {showPopups && (
            <Popup maxWidth={300} className="enhanced-popup">
              <div style={{ padding: '12px', minWidth: '200px' }}>
                <h3 style={{ fontWeight: 'bold', margin: '0 0 8px 0', color: '#1f2937' }}>
                  {property.name}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '16px' }}>{getTypeEmoji(property.type)}</span>
                  <span style={{ fontSize: '12px', color: '#6b7280', textTransform: 'capitalize' }}>
                    {property.type}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
                  <div>
                    <p style={{ margin: '0', fontSize: '11px', color: '#6b7280' }}>Flood Depth</p>
                    <p style={{ margin: '0', fontSize: '14px', fontWeight: '600' }}>{property.floodDepth}m</p>
                  </div>
                  <div>
                    <p style={{ margin: '0', fontSize: '11px', color: '#6b7280' }}>Risk Level</p>
                    <p style={{
                      margin: '0',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: getVulnerabilityColor(property.vulnerability),
                      textTransform: 'capitalize'
                    }}>
                      {property.vulnerability}
                    </p>
                  </div>
                </div>
                <div style={{
                  marginTop: '8px',
                  paddingTop: '8px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <p style={{ margin: '0', fontSize: '11px', color: '#6b7280' }}>Property Value</p>
                  <p style={{ margin: '0', fontSize: '14px', fontWeight: '600' }}>
                    ₹{(property.value / 100000).toFixed(1)}L
                  </p>
                </div>
              </div>
            </Popup>
          )}
        </Marker>
      ))}
    </>
  )
}
